/**
 * How one finished order reads in the list — a 1:1 port of Android's `OrderHistoryPresentation`.
 *
 * The branching is the backend contract's, not re-derived: a billed completion wins over
 * everything; failing that a charged cancellation; failing that a no-show; then the two states
 * that are neither («на разборе», «истёк»); anything left is a free cancellation. Getting the
 * order wrong produces rows that are individually plausible and collectively wrong — a cancelled
 * order that took a fee reading as «Отменён», with the money nowhere on screen, or an order still
 * sitting on a dispatcher's desk telling its owner it was cancelled.
 */
import { formatLiters, formatMoney } from '@/shared/lib/format';
import { NO_SHOW_STATE } from './tracking';

/** `OrderState` values that are neither a completion nor a cancellation. */
const PROBLEMATIC_STATE = 'PROBLEMATIC';
const EXPIRED_STATE = 'EXPIRED';

export type OrderHistoryRowKind =
  | 'completed'
  | 'canceledWithFee'
  | 'noShow'
  /**
   * Не отменён и не завершён: заявка на разборе или с истёкшим сроком.
   *
   * Своя разновидность, потому что обе раньше сваливались в «Отменён» — а заказ, который висит на
   * разборе у диспетчера, отменённым не является, и человек, увидевший «Отменён», перестанет его
   * ждать.
   */
  | 'unresolved'
  | 'canceledFree';

export interface OrderHistoryRow {
  kind: OrderHistoryRowKind;
  title: string;
  pillLabel: string;
}

export function historyRow(input: {
  state: string;
  totalPrice: string | null;
  fuelAmountLiters: string | null;
  chargedOnCancellation: boolean;
  cancellationFeeAmount: string;
}): OrderHistoryRow {
  if (input.totalPrice !== null) {
    const liters = formatLiters(input.fuelAmountLiters ?? '');
    const price = formatMoney(input.totalPrice);
    return {
      kind: 'completed',
      title: `${liters} · ${price}`,
      pillLabel: 'Завершён',
    };
  }

  if (input.chargedOnCancellation) {
    return {
      kind: 'canceledWithFee',
      title: `Отменён · ${formatMoney(input.cancellationFeeAmount)}`,
      pillLabel: 'Отмена',
    };
  }

  if (input.state === NO_SHOW_STATE) {
    return { kind: 'noShow', title: 'Заправщик не нашёл технику', pillLabel: 'No-show' };
  }

  if (input.state === PROBLEMATIC_STATE) {
    return { kind: 'unresolved', title: 'Заявка на разборе', pillLabel: 'Разбор' };
  }

  if (input.state === EXPIRED_STATE) {
    return { kind: 'unresolved', title: 'Срок заявки истёк', pillLabel: 'Истёк' };
  }

  return { kind: 'canceledFree', title: 'Отменён', pillLabel: 'Отмена' };
}

/**
 * The car half of a row.
 *
 * «Camry +2» because the list endpoint carries one denormalized car and a count — naming only the
 * first would make a three-car выезд indistinguishable from a one-car one in somebody's own
 * history.
 */
export function historyCarLabel(brand: string, model: string, carCount: number): string {
  const extras = carCount - 1;
  const name = `${brand} ${model}`.trim();
  return extras > 0 ? `${name} +${extras}` : name;
}

const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const FULL_DATE = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * «Сегодня, 14:32» / «Вчера, …» / «3 июля, 09:14» / «28.06.2025, 18:02».
 *
 * Capitalised, unlike the notification centre's mid-sentence «сегодня»: here it starts the line.
 * An unparseable timestamp renders as an empty string — one broken row must not take the list
 * down with it.
 */
export function historyDateTimeLabel(createdAt: string, now: Date = new Date()): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const time = TIME.format(date);
  if (sameDay(date, now)) return `Сегодня, ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `Вчера, ${time}`;

  if (date.getFullYear() === now.getFullYear()) return `${DAY_MONTH.format(date)}, ${time}`;
  return `${FULL_DATE.format(date)}, ${time}`;
}

/**
 * The detail screen's title date, without a time.
 *
 * Real order ids are UUIDs — there is no human-friendly «Заказ №2481» to title with, so the date
 * does the job.
 */
export function historyTitleDateLabel(createdAt: string, now: Date = new Date()): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.getFullYear() === now.getFullYear()
    ? DAY_MONTH.format(date)
    : FULL_DATE.format(date);
}
